import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Select,
  InputNumber,
  Space,
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  RollbackOutlined,
} from "@ant-design/icons";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

import { getAPI, postAPI, displayMessage } from "../../utils/common";
import { SUCCESS_MESSAGE, ERROR_MSG_TYPE } from "../../constants/messages";
import { KITCHEN_ITEM_LIST, KITCHEN_PR_CREATE } from "../../constants/api";
import LS_SERVICE from "../../utils/localStorage";

function KitchenPRAdd() {
  const { Content } = Layout;
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const selectedItems = Form.useWatch("items", form) || [];
  const selectedIds = selectedItems.map((i) => i?.item_id).filter(Boolean);

  useEffect(() => {
    fetchItems();
    form.setFieldsValue({
      requested_by: LS_SERVICE.get("name"),
    });
  }, []);

  const fetchItems = () => {
    getAPI(
      KITCHEN_ITEM_LIST,
      (res) => {
        if (res.status === true) setItems(res.data);
      },
      () => {},
    );
  };

  const createData = (values) => {
    setLoading(true);

    const payload = {
      requested_by: values.requested_by,
      priority: values.priority,
      remark: values.remark,
      items: values.items.map((row) => ({
        item_id: row.item_id,
        item_requried_stock: row.item_requried_stock,
        item_remark: row.item_remark,
      })),
    };

    postAPI(
      KITCHEN_PR_CREATE,
      payload,
      (res) => {
        if (res.status === true) {
          displayMessage(SUCCESS_MESSAGE, res.message);
          navigate("/kitchen/purchase-request");
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to create PR");
      },
    );
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Add Purchase Request"
          bordered={false}
          extra={
            <Button type="primary" ghost onClick={() => navigate(-1)}>
              <RollbackOutlined />
            </Button>
          }
        >
          <Spin spinning={loading}>
            <Form
              form={form}
              layout="vertical"
              onFinish={createData}
              initialValues={{ priority: "Normal", items: [{}] }}
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item
                    name="requested_by"
                    label="Requested By"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Enter requester name" />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name="priority"
                    label="Priority"
                    rules={[{ required: true }]}
                  >
                    <Select>
                      <Select.Option value="Normal">Normal</Select.Option>
                      <Select.Option value="Urgent">Urgent</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={10}>
                  <Form.Item name="remark" label="Remark">
                    <Input placeholder="Optional remark" />
                  </Form.Item>
                </Col>
              </Row>

              <Card title="Requested Items" size="small">
                <Form.List name="items">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={[16, 16]} key={key}>
                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, "item_id"]}
                              label="Item"
                              rules={[{ required: true }]}
                            >
                              <Select
                                placeholder="Select item"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  option?.children
                                    ?.toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              >
                                {items.map((item) => {
                                  const isDisabled = selectedIds.includes(
                                    item.id,
                                  );

                                  return (
                                    <Select.Option
                                      key={item.id}
                                      value={item.id}
                                      disabled={isDisabled}
                                    >
                                      {item.name}
                                    </Select.Option>
                                  );
                                })}
                              </Select>
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item shouldUpdate noStyle>
                              {({ getFieldValue }) => {
                                const itemId = getFieldValue([
                                  "items",
                                  name,
                                  "item_id",
                                ]);

                                const selectedItem = items.find(
                                  (item) => item.id === itemId,
                                );

                                return (
                                  <Form.Item
                                    {...restField}
                                    name={[name, "item_requried_stock"]}
                                    label="Required Qty"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Qty is required",
                                      },
                                      {
                                        validator: (_, value) => {
                                          if (
                                            value === undefined ||
                                            value === null
                                          ) {
                                            return Promise.resolve();
                                          }

                                          // allow positive numbers including decimals like 0.5
                                          const regex =
                                            /^(0\.\d+|[1-9]\d*(\.\d+)?)$/;

                                          return regex.test(value)
                                            ? Promise.resolve()
                                            : Promise.reject(
                                                new Error(
                                                  "Only positive numbers allowed",
                                                ),
                                              );
                                        },
                                      },
                                    ]}
                                  >
                                    <InputNumber
                                      min={0.001}
                                      step={0.001}
                                      precision={3}
                                      suffix={`${selectedItem?.unit ? `(${selectedItem.unit})` : ""}`}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Col>

                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "item_remark"]}
                              label="Remark"
                            >
                              <Input placeholder="Optional remark" />
                            </Form.Item>
                          </Col>

                          <Col
                            span={2}
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Button
                              danger
                              onClick={() => remove(name)}
                              icon={<MinusCircleOutlined />}
                            />
                          </Col>
                        </Row>
                      ))}

                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Item
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Card>

              <Row>
                <Col span={24}>
                  <Form.Item style={{ float: "right", marginTop: 16 }}>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Submit PR
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default KitchenPRAdd;
